import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { Utils } from '@/utils/utils';
import { ChangeDetectorRef, Component, Input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

interface TermDraft {
  id?: number;
  value: string;
}

interface AttributeDraft {
  id?: number;
  name: string;
  terms: TermDraft[];
  newTerm: string;
  dirty: boolean;
}

/**
 * Atributos de variación (Talla, Color...) de un tipo de producto y sus términos (S, M, L...).
 * Cada atributo se guarda por separado contra `product-types/{id}/attributes`: el PUT sincroniza
 * los términos (con `id` se actualizan, sin `id` se crean, los que faltan se eliminan - el
 * backend responde 422 si alguno lo usa una variación).
 */
@Component({
  selector: 'app-product-type-attributes',
  imports: [FormsModule],
  templateUrl: './product-type-attributes.component.html',
})
export class ProductTypeAttributesComponent implements OnInit {
  @Input({ required: true }) productTypeId!: number | string;

  public loading = signal(false);
  public attributes: AttributeDraft[] = [];

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.load();
  }

  private get url(): string {
    return `${Urls.urls.productTypes}/${this.productTypeId}/attributes`;
  }

  load() {
    this.loading.set(true);
    this.apiService.getPetition(this.url).subscribe({
      next: (data: any) => {
        this.attributes = (data.list || []).map((attribute: any) => this.toDraft(attribute));
        this.loading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading.set(false);
        Utils.notification.alert('Error al obtener los atributos');
      },
    });
  }

  addAttribute() {
    this.attributes.push({ name: '', terms: [], newTerm: '', dirty: true });
  }

  addTerm(attribute: AttributeDraft) {
    const values = attribute.newTerm.split(',').map(value => value.trim()).filter(Boolean);
    for (const value of values) {
      if (!attribute.terms.some(term => term.value.toLowerCase() === value.toLowerCase())) {
        attribute.terms.push({ value });
      }
    }
    attribute.newTerm = '';
    attribute.dirty = true;
  }

  removeTerm(attribute: AttributeDraft, index: number) {
    attribute.terms.splice(index, 1);
    attribute.dirty = true;
  }

  saveAttribute(attribute: AttributeDraft) {
    if (attribute.newTerm.trim()) {
      this.addTerm(attribute);
    }

    if (!attribute.name.trim()) {
      Utils.notification.alert('El atributo necesita un nombre');
      return;
    }

    const body = {
      name: attribute.name.trim(),
      terms: attribute.terms.map((term, index) => ({ id: term.id, value: term.value, sort_order: index })),
    };

    const request$ = attribute.id
      ? this.apiService.put(`${this.url}/${attribute.id}`, body)
      : this.apiService.post(this.url, body);

    this.loading.set(true);
    request$.subscribe({
      next: (saved: any) => {
        Object.assign(attribute, this.toDraft(saved));
        this.loading.set(false);
        Utils.notification.success('Atributo guardado');
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.loading.set(false);
        Utils.notification.alert(Utils.extractErrorMessage(error, 'name') || 'Error al guardar el atributo');
      },
    });
  }

  deleteAttribute(attribute: AttributeDraft, index: number) {
    if (!attribute.id) {
      this.attributes.splice(index, 1);
      return;
    }

    this.loading.set(true);
    this.apiService.delete(`${this.url}/${attribute.id}`).subscribe({
      next: () => {
        this.attributes.splice(index, 1);
        this.loading.set(false);
        Utils.notification.success('Atributo eliminado');
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.loading.set(false);
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al eliminar el atributo');
      },
    });
  }

  private toDraft(attribute: any): AttributeDraft {
    return {
      id: attribute.id,
      name: attribute.name,
      terms: (attribute.terms || []).map((term: any) => ({ id: term.id, value: term.value })),
      newTerm: '',
      dirty: false,
    };
  }
}
